/**
 * Shared two-column layout for all chat-based creation flows.
 *
 * Left column (5/12 at lg+): scrollable chat thread — assistant messages,
 *   user replies, inline inputs and action buttons.
 * Right column (7/12 at lg+): live status / output panel — pipeline steps,
 *   generated media, editor views.
 *
 * On small screens the columns stack: left chat on top, right panel below.
 */
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

import { TwinneeLogo } from '@/components/TwinneeLogo';
/* ── Chat bubble helpers ─────────────────────────────────────────────────── */

/**
 * Assistant (Twinnee) chat bubble.
 * @param {{ children: React.ReactNode, first?: boolean }} props
 */
export function AssistantBubble({ children, first = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className={`flex gap-2.5 ${first ? '' : 'mt-1'}`}
    >
      <div className="w-7 h-7 rounded-full bg-[#2DD4BF]/15 border border-[#2DD4BF]/25 flex items-center justify-center shrink-0 mt-0.5">
        <TwinneeLogo className="w-3.5 h-3.5 text-[#2DD4BF]" />
      </div>
      <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-[#111827] border border-white/[0.07] px-3.5 py-2.5 text-[13px] text-[#E5E7EB] leading-relaxed">
        {children}
      </div>
    </motion.div>
  );
}

/**
 * User reply bubble (right-aligned).
 * @param {{ children: React.ReactNode }} props
 */
export function UserBubble({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className="flex justify-end mt-1"
    >
      <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-[#2DD4BF]/15 border border-[#2DD4BF]/25 px-3.5 py-2 text-[13px] text-[#2DD4BF] leading-relaxed">
        {children}
      </div>
    </motion.div>
  );
}

/**
 * Inline action area below assistant messages — buttons, inputs, etc.
 */
export function ChatInputArea({ children }) {
  return (
    <div className="ml-9 mt-2 mb-1">
      {children}
    </div>
  );
}

/**
 * Divider / section label inside the chat thread.
 */
export function ChatSection({ label }) {
  return (
    <div className="flex items-center gap-2 my-3 ml-9">
      <div className="flex-1 h-px bg-white/[0.06]" />
      {label ? <span className="text-[10px] text-[#6B7280] font-mono uppercase tracking-wider">{label}</span> : null}
      <div className="flex-1 h-px bg-white/[0.06]" />
    </div>
  );
}

/* ── Right panel heading ────────────────────────────────────────────────── */

export function RightPanelHeading({ children }) {
  return (
    <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-widest px-5 pt-4 pb-2">
      {children}
    </p>
  );
}

/* ── Main layout ─────────────────────────────────────────────────────────── */

/**
 * FlowChatLayout
 *
 * @param {{
 *   flowLabel: string,
 *   flowColor?: string,
 *   onBack?: () => void,
 *   backLabel?: string,
 *   headerRight?: React.ReactNode,
 *   leftContent: React.ReactNode,
 *   rightContent: React.ReactNode,
 *   rightEmpty?: React.ReactNode,
 *   showRight?: boolean,
 * }} props
 */
export function FlowChatLayout({
  flowLabel,
  flowColor = '#2DD4BF',
  onBack,
  backLabel = 'Dashboard',
  headerRight,
  leftContent,
  rightContent,
  rightEmpty,
  showRight = true,
}) {
  return (
    <div
      className="min-h-screen flex flex-col bg-[#07090d] text-white"
      style={{ '--flow-color': flowColor }}
    >
      {/* ── Header ── */}
      <div className="h-12 shrink-0 border-b border-white/[0.08] bg-[#0b0e14]/95 backdrop-blur-xl flex items-center justify-between px-3 sm:px-5 z-10">
        <div className="flex items-center gap-2 min-w-0">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="h-7 px-2 rounded-lg border border-white/10 text-[#9CA3AF] hover:text-white hover:bg-white/5 flex items-center gap-1 text-[11px] cursor-pointer transition-colors mr-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{backLabel}</span>
            </button>
          ) : null}
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center"
            style={{ background: `${flowColor}22`, border: `1px solid ${flowColor}44` }}
          >
            <TwinneeLogo className="w-3 h-3" style={{ color: flowColor }} />
          </div>
          <span className="text-sm font-semibold text-white truncate">{flowLabel}</span>
        </div>
        {headerRight ? (
          <div className="flex items-center gap-2">{headerRight}</div>
        ) : null}
      </div>

      {/* ── Body ── */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden" style={{ minHeight: 0 }}>
        {/* Left — chat thread */}
        <div className="lg:col-span-5 flex flex-col min-h-0 border-r border-white/[0.07] bg-[#0a0c11] overflow-y-auto">
          <div className="flex-1 px-4 sm:px-5 py-5 space-y-1">
            {leftContent}
          </div>
        </div>

        {/* Right — status / output */}
        <div className="lg:col-span-7 flex flex-col min-h-0 bg-[#09090e] overflow-y-auto">
          <AnimatePresence mode="wait">
            {showRight ? (
              <motion.div
                key="right-content"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
                className="flex-1"
              >
                {rightContent}
              </motion.div>
            ) : (
              <motion.div
                key="right-empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 flex items-center justify-center"
              >
                {rightEmpty || (
                  <div className="text-center space-y-2 px-8">
                    <div
                      className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center"
                      style={{ background: `${flowColor}15`, border: `1px solid ${flowColor}30` }}
                    >
                      <TwinneeLogo className="w-5 h-5" style={{ color: flowColor }} />
                    </div>
                    <p className="text-[13px] text-[#6B7280]">
                      Your output will appear here once generation starts.
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default FlowChatLayout;
